import { AgendaItem as AgendaItemType } from '../../types';
import { Badge } from '../UI/Badge';
import styles from './AgendaItem.module.css';

interface AgendaItemProps {
  item: AgendaItemType;
  isActive: boolean;
  isPast: boolean;
}

/**
 * AgendaItem Component
 * 
 * Displays a single agenda item in the timeline with:
 * - Start time and duration/end time
 * - Title and description
 * - Active styling with fire accent left border
 * - Dimmed styling for past items
 * - Timeline indicator pattern
 * 
 * Validates Requirements: 6.2, 6.3, 6.4, 6.5
 */
export function AgendaItem({ item, isActive, isPast }: AgendaItemProps) {
  const formatTime = (date: Date): string => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  const calculateDuration = (start: Date, end: Date): string => {
    const durationMs = end.getTime() - start.getTime();
    const durationMinutes = Math.floor(durationMs / 1000 / 60);
    const hours = Math.floor(durationMinutes / 60);
    const minutes = durationMinutes % 60;

    if (hours > 0) {
      return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
    }
    return `${minutes}m`;
  };

  const startTime = formatTime(item.startTime);
  const endTime = formatTime(item.endTime);
  const duration = calculateDuration(item.startTime, item.endTime);

  return (
    <div 
      className={`${styles.agendaItem} ${isActive ? styles.active : ''} ${isPast ? styles.past : ''}`}
      data-testid={`agenda-item-${item.id}`}
    >
      {/* Timeline indicator */}
      <div className={styles.timelineIndicator}>
        <div className={styles.timelineDot} />
        <div className={styles.timelineLine} />
      </div>

      {/* Content */}
      <div className={styles.content}>
        {/* Time information */}
        <div className={styles.timeInfo}>
          <span className={styles.startTime}>{startTime}</span>
          <span className={styles.separator}>•</span>
          <span className={styles.duration}>{duration}</span>
          <span className={styles.separator}>•</span>
          <span className={styles.endTime}>{endTime}</span>
        </div>

        {/* Title */}
        <div className={styles.titleRow}>
          <h3 className={styles.title}>{item.title}</h3>
          {item.requiresTeams && (
            <Badge variant="fire" className={styles.teamBadge}>
              Teams
            </Badge>
          )}
          {item.requiresCycle && (
            <Badge variant="default" className={styles.cycleBadge}>
              Cycle
            </Badge>
          )}
        </div>

        {/* Additional info (if present) */}
        {item.additionalInfo && (
          <p className={styles.additionalInfo}>{item.additionalInfo}</p>
        )}
      </div>
    </div>
  );
}
